import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useLeads, useLeadMutations, useActivityMutations } from '../hooks/useCRM';
import { exportLeads, importLeads, getLeadViews, saveLeadView, getTeam } from '../utils/crmApi';
import LeadTable from '../components/LeadTable';
import LeadCard from '../components/LeadCard';
import LeadForm from '../components/LeadForm';
import ImportModal from '../components/ImportModal';
import ActivityForm from '../components/ActivityForm';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import { getStoredAuthUser, resolveAuthUserName } from '../utils/leadOwner';
import { isCrmPortalMode, leadSourcesPath } from '../utils/crmEnv';
import '../crm.css';

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'unqualified', label: 'Unqualified' },
    { value: 'converted', label: 'Converted' },
];

const QUICK_STATUS = [
    { value: '', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'unqualified', label: 'Unqualified' },
    { value: 'converted', label: 'Converted' },
];

const SOURCE_OPTIONS = [
    { value: '', label: 'All Sources' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'cold_call', label: 'Cold Call' },
    { value: 'advertisement', label: 'Advertisement' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'walk_in', label: 'Walk-in' },
    { value: 'other', label: 'Other' },
];

const DEFAULT_FILTERS = {
    search: '',
    status: '',
    source: '',
    owner: '',
    scoreMin: '',
    scoreMax: '',
    tags: '',
    createdFrom: '',
    createdTo: '',
    needsFollowUp: false,
};

const STORAGE_KEYS = {
    lastView: 'crm:leads:lastView',
};

const LEADS_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

const loadLastView = () => {
    if (typeof window === 'undefined') return 'default';
    return window.localStorage.getItem(STORAGE_KEYS.lastView) || 'default';
};


const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const next = line[i + 1];
        if (inQuotes) {
            if (char === '"' && next === '"') {
                current += '"';
                i += 1;
            } else if (char === '"') {
                inQuotes = false;
            } else {
                current += char;
            }
            continue;
        }
        if (char === '"') {
            inQuotes = true;
            continue;
        }
        if (char === ',') {
            result.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    result.push(current);
    return result.map((entry) => entry.trim());
};

const parseCsvHeaders = (text) => {
    if (!text) return [];
    const firstLine = text.split(/\r?\n/).find((line) => line.trim().length > 0) || '';
    return parseCsvLine(firstLine);
};

const applyViewFilters = (view) => {
    if (!view) return { ...DEFAULT_FILTERS };
    const query = view.query || {};
    const normalizedStatus = query.status === 'all' ? '' : query.status;
    const next = {
        ...DEFAULT_FILTERS,
        ...(query.filters || {}),
        status: normalizedStatus || '',
        search: query.q || '',
    };
    return next;
};

const createActivitySeed = (lead) => {
    const name = lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';
    return {
        type: 'task',
        title: `Follow up with ${name}`,
        description: '',
        status: 'pending',
        dueDate: '',
        entityType: 'lead',
        entityId: lead.id,
        entityName: name,
    };
};

const LeadsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const authUser = useMemo(() => getStoredAuthUser(), []);

    const [viewMode, setViewMode] = useState('table');
    const [showModal, setShowModal] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [views, setViews] = useState([]);
    const [selectedView, setSelectedView] = useState(loadLastView);
    const [sortConfig, setSortConfig] = useState({ sort: 'createdAt', dir: 'desc' });

    const { leads, total, loading, error, updateParams, refetch } = useLeads({
        page: 1,
        pageSize,
        sort: 'createdAt',
        dir: 'desc',
    });

    const { create, update, remove, executeAction, loading: mutationLoading } = useLeadMutations();
    const { create: createActivity, loading: activityLoading } = useActivityMutations();
    const { showSuccess, showError, showInfo } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkAssignee, setBulkAssignee] = useState('');
    const [bulkTag, setBulkTag] = useState('');
    const [bulkProgress, setBulkProgress] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [assignmentOptions, setAssignmentOptions] = useState([]);

    const [activityLead, setActivityLead] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showLeadEntryMenu, setShowLeadEntryMenu] = useState(false);
    const leadEntryMenuRef = useRef(null);

    useEffect(() => {
        const loadViews = async () => {
            try {
                const data = await getLeadViews();
                const list = Array.isArray(data.views) ? data.views : [];
                setViews(list);
                if (!list.find((view) => view.viewId === selectedView)) {
                    setSelectedView('default');
                }
            } catch (err) {
                showError(err.message || 'Failed to load views');
            }
        };
        loadViews();
    }, [selectedView, showError]);

    useEffect(() => {
        const loadAssignmentOptions = async () => {
            if (isCrmPortalMode()) {
                setAssignmentOptions([]);
                return;
            }
            try {
                const team = await getTeam();
                const seats = Array.isArray(team?.seats) ? team.seats : [];
                const uniqueBySeatId = new Map();
                seats.forEach((seat) => {
                    const role = String(seat?.role || '').toLowerCase();
                    const status = String(seat?.status || 'active').toLowerCase();
                    if (!(role === 'agent' || role === 'manager' || role === 'admin') || status !== 'active') {
                        return;
                    }
                    const seatId = String(seat?.id || '').trim();
                    if (!seatId || uniqueBySeatId.has(seatId)) {
                        return;
                    }
                    const name = String(seat?.name || seat?.alias || seatId).trim();
                    uniqueBySeatId.set(seatId, {
                        seatId,
                        seatUid: `seat:${seatId}`,
                        name,
                    });
                });
                const options = Array.from(uniqueBySeatId.values()).sort((a, b) => a.name.localeCompare(b.name));
                setAssignmentOptions(options);
            } catch (error) {
                // Assignment options are optional; keep page usable if team lookup is unavailable.
                console.warn('Unable to load assignment options:', error);
                setAssignmentOptions([]);
            }
        };

        loadAssignmentOptions();
    }, []);

    useEffect(() => {
        const view = views.find((entry) => entry.viewId === selectedView);
        if (!view) {
            return;
        }
        const nextFilters = applyViewFilters(view);
        setFilters(nextFilters);
        setSearchInput(nextFilters.search || '');
        setPage(1);
        if (view.query?.sort || view.query?.dir) {
            setSortConfig((prev) => ({
                sort: view.query?.sort || prev.sort,
                dir: view.query?.dir || prev.dir,
            }));
        }
    }, [selectedView, views]);

    useEffect(() => {
        updateParams({
            q: filters.search,
            status: filters.status || 'all',
            source: filters.source,
            owner: filters.owner,
            scoreMin: filters.scoreMin,
            scoreMax: filters.scoreMax,
            tags: filters.tags,
            createdFrom: filters.createdFrom,
            createdTo: filters.createdTo,
            needsFollowUp: filters.needsFollowUp,
            viewId: selectedView,
            page,
            pageSize,
            sort: sortConfig.sort,
            dir: sortConfig.dir,
        });
    }, [
        filters.search,
        filters.status,
        filters.source,
        filters.owner,
        filters.scoreMin,
        filters.scoreMax,
        filters.tags,
        filters.createdFrom,
        filters.createdTo,
        filters.needsFollowUp,
        page,
        pageSize,
        selectedView,
        sortConfig,
        updateParams,
    ]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters((prev) => ({ ...prev, search: searchInput }));
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [pageSize]);

    useEffect(() => {
        const handleQuickAdd = () => setShowModal(true);
        window.addEventListener('crm:quickAddLead', handleQuickAdd);
        const handleLeadsRefresh = () => refetch();
        window.addEventListener('crm:leads:refresh', handleLeadsRefresh);
        return () => {
            window.removeEventListener('crm:quickAddLead', handleQuickAdd);
            window.removeEventListener('crm:leads:refresh', handleLeadsRefresh);
        };
    }, [refetch]);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, filters, viewMode, location.pathname]);

    useEffect(() => {
        if (!showLeadEntryMenu) return undefined;
        const handleOutsideClick = (event) => {
            if (!leadEntryMenuRef.current) return;
            if (!leadEntryMenuRef.current.contains(event.target)) {
                setShowLeadEntryMenu(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showLeadEntryMenu]);

    const hasActiveFilters = Boolean(
        filters.search ||
        filters.status ||
        filters.source ||
        filters.owner ||
        filters.scoreMin ||
        filters.scoreMax ||
        filters.tags ||
        filters.createdFrom ||
        filters.createdTo ||
        filters.needsFollowUp
    );
    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pageStart = total > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
    const pageEnd = total > 0 ? Math.min(total, currentPage * pageSize) : 0;

    useEffect(() => {
        if (!loading && page > totalPages) {
            setPage(totalPages);
        }
    }, [loading, page, totalPages]);

    const handleViewChange = (event) => {
        const nextView = event.target.value;
        setSelectedView(nextView);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEYS.lastView, nextView);
        }
    };

    const handleSaveView = () => {
        const name = window.prompt('Save this view as:', 'New View');
        if (!name) return;
        const trimmed = name.trim();
        if (!trimmed) return;
        const payload = {
            name: trimmed,
            shared: false,
            query: {
                q: filters.search || undefined,
                status: filters.status || undefined,
                sort: sortConfig.sort,
                dir: sortConfig.dir,
                pageSize,
                filters: {
                    source: filters.source || undefined,
                    owner: filters.owner || undefined,
                    scoreMin: filters.scoreMin || undefined,
                    scoreMax: filters.scoreMax || undefined,
                    tags: filters.tags || undefined,
                    createdFrom: filters.createdFrom || undefined,
                    createdTo: filters.createdTo || undefined,
                    needsFollowUp: filters.needsFollowUp || undefined,
                },
            },
        };

        saveLeadView(payload)
            .then((saved) => {
                setSelectedView(saved.viewId);
                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(STORAGE_KEYS.lastView, saved.viewId);
                }
                return getLeadViews().then((data) => setViews(Array.isArray(data.views) ? data.views : []));
            })
            .then(() => showSuccess('View saved'))
            .catch((err) => showError(err.message || 'Failed to save view'));
    };

    const handleQuickActionLog = async (lead, channel) => {
        if (!lead?.id) return;

        const phoneRaw = String(lead.phone || lead.mobile || '').trim();
        const whatsapp = phoneRaw.replace(/[^0-9]/g, '');
        const email = String(lead.email || '').trim();

        let url = '';
        let payload = null;
        let actionLabel = '';

        if (channel === 'call') {
            if (!phoneRaw) return;
            url = `tel:${phoneRaw}`;
            actionLabel = 'Call lead';
            payload = { type: 'log_call', note: actionLabel };
        } else if (channel === 'whatsapp') {
            if (!whatsapp) return;
            url = `https://wa.me/${whatsapp}`;
            actionLabel = 'WhatsApp lead';
            payload = { type: 'send_whatsapp', message: actionLabel };
        } else if (channel === 'email') {
            if (!email) return;
            url = `mailto:${email}`;
            actionLabel = 'Email lead';
            payload = { type: 'send_email', subject: actionLabel, body: '' };
        } else {
            return;
        }

        if (/^(tel:|mailto:)/i.test(url) && typeof document !== 'undefined') {
            const link = document.createElement('a');
            link.href = url;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }

        try {
            const requestKey = `table-quick-${channel}-${lead.id}-${Math.floor(Date.now() / 1000)}`;
            await executeAction(lead.id, payload, requestKey);
        } catch (err) {
            console.error('Failed to log lead quick action:', err);
        }

        refetch({ silent: true });
    };
    const handleStatusChange = async (leadId, newStatus) => {
        try {
            await update(leadId, { status: newStatus });
            showSuccess(`Lead status updated to ${newStatus}`);
            refetch();
        } catch (err) {
            console.error('Failed to update status:', err);
            showError('Failed to update lead status');
        }
    };

    const handleEdit = (lead) => {
        setEditingLead(lead);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Delete Lead',
            message: 'Are you sure you want to delete this lead? This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'danger',
        });

        if (isConfirmed) {
            try {
                await remove(id);
                showSuccess('Lead deleted successfully');
                refetch();
            } catch (err) {
                console.error('Failed to delete lead:', err);
                showError('Failed to delete lead');
            }
        }
    };

    const handleSubmit = async (formData) => {
        try {
            if (editingLead) {
                await update(editingLead.id, formData);
                showSuccess('Lead updated successfully');
            } else {
                const ownerName = resolveAuthUserName(authUser);
                const payload = {
                    ...formData,
                    ...(authUser?.uid ? { assignedTo: authUser.uid } : {}),
                    ...(ownerName ? { assignedToName: ownerName } : {}),
                };
                await create(payload);
                showSuccess('Lead created successfully');
            }
            setShowModal(false);
            setEditingLead(null);
            refetch();
        } catch (err) {
            console.error('Failed to save lead:', err);
            showError('Failed to save lead');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLead(null);
    };

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setSearchInput('');
        setPage(1);
        setSelectedView('default');
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEYS.lastView, 'default');
        }
    };

    const handleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const handleSelectAll = (ids, checked) => {
        if (checked) {
            setSelectedIds(new Set(ids));
        } else {
            setSelectedIds(new Set());
        }
    };

    const runBulkUpdate = async (ids, updater, label) => {
        if (ids.length === 0) return;
        setBulkLoading(true);
        setBulkProgress({ label, current: 0, total: ids.length });

        let completed = 0;
        for (const id of ids) {
            try {
                await updater(id);
            } catch (err) {
                console.error('Bulk update error:', err);
                showError('One or more updates failed');
            }
            completed += 1;
            setBulkProgress({ label, current: completed, total: ids.length });
            showInfo(`${completed}/${ids.length} ${label}...`);
        }

        setBulkLoading(false);
        setBulkProgress(null);
        setSelectedIds(new Set());
        refetch();
        showSuccess(`${label} completed`);
    };

    const handleBulkStatus = async (statusValue) => {
        if (!statusValue || selectedIds.size === 0) return;
        await runBulkUpdate(Array.from(selectedIds), (id) => update(id, { status: statusValue }), 'Updated');
    };

    const handleBulkAssign = async () => {
        if (!bulkAssignee || selectedIds.size === 0) return;
        const selected = assignmentOptions.find((option) => option.seatUid === bulkAssignee);
        if (!selected) return;
        await runBulkUpdate(
            Array.from(selectedIds),
            (id) => update(id, {
                assignedTo: selected.seatUid,
                assignedToName: selected.name,
                ownerSeatId: selected.seatId,
            }),
            'Assigned',
        );
        setBulkAssignee('');
    };

    const handleBulkAddTag = async () => {
        const tag = bulkTag.trim();
        if (!tag || selectedIds.size === 0) return;
        await runBulkUpdate(
            Array.from(selectedIds),
            (id) => {
                const lead = leads.find((item) => item.id === id);
                const tags = Array.isArray(lead?.tags) ? lead.tags : [];
                const nextTags = Array.from(new Set([...tags, tag]));
                return update(id, { tags: nextTags });
            },
            'Tagged',
        );
        setBulkTag('');
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        const isConfirmed = await confirm({
            title: 'Delete Selected Leads',
            message: `Delete ${selectedIds.size} selected leads? This action cannot be undone.`,
            confirmText: 'Delete',
            variant: 'danger',
        });

        if (!isConfirmed) return;

        await runBulkUpdate(Array.from(selectedIds), (id) => remove(id), 'Deleted');
    };

    const handleAddTask = (lead) => {
        setActivityLead(lead);
    };

    const handleActivitySubmit = async (formData) => {
        if (!activityLead) return;
        try {
            await createActivity({
                ...formData,
                entityType: 'lead',
                entityId: activityLead.id,
                entityName: formData.entityName || activityLead.name,
            });
            showSuccess('Activity created');
            setActivityLead(null);
            refetch();
        } catch (err) {
            console.error('Failed to create activity:', err);
            showError('Failed to create activity');
        }
    };

    const handleImportClick = () => {
        setShowImportModal(true);
    };

    const handleExport = async () => {
        try {
            const blob = await exportLeads({
                search: filters.search,
                status: filters.status,
                source: filters.source,
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'leads-export.csv';
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            showError(err.message || 'Failed to export leads');
        }
    };


    const leadFieldOptions = [
        { value: '', label: 'Ignore' },
        { value: 'firstName', label: 'First Name' },
        { value: 'lastName', label: 'Last Name' },
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Phone' },
        { value: 'mobile', label: 'Mobile' },
        { value: 'company', label: 'Company' },
        { value: 'jobTitle', label: 'Job Title' },
        { value: 'status', label: 'Status' },
        { value: 'source', label: 'Source' },
        { value: 'score', label: 'Score' },
        { value: 'assignedTo', label: 'Assigned To' },
        { value: 'assignedToName', label: 'Assigned To Name' },
        { value: 'tags', label: 'Tags' },
        { value: 'notes', label: 'Notes' },
        { value: 'address', label: 'Address' },
        { value: 'city', label: 'City' },
        { value: 'state', label: 'State' },
        { value: 'country', label: 'Country' },
    ];

    return (
        <div className="crm-container">
            <div className="crm-toolbar">
                <div className="crm-toolbar-row">
                    <div className="crm-toolbar-title">
                        <h1 className="crm-page-title" style={{ margin: 0 }}>Leads</h1>
                        <span className="count-badge">{total || 0}</span>
                        <select
                            className="crm-select crm-select-sm"
                            value={selectedView}
                            onChange={handleViewChange}
                        >
                            {views.map((view) => (
                                <option key={view.viewId} value={view.viewId}>{view.name}</option>
                            ))}
                        </select>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={handleSaveView}>
                            Save view
                        </button>
                    </div>
                    <div className="crm-toolbar-actions">
                        <div className="crm-view-toggle">
                            <button
                                className={`crm-btn crm-btn-sm ${viewMode === 'table' ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                                onClick={() => setViewMode('table')}
                                type="button"
                            >
                                Table
                            </button>
                            <button
                                className={`crm-btn crm-btn-sm ${viewMode === 'cards' ? 'crm-btn-primary' : 'crm-btn-secondary'}`}
                                onClick={() => setViewMode('cards')}
                                type="button"
                            >
                                Cards
                            </button>
                        </div>
                        {!isCrmPortalMode() && (
                            <button
                                className="crm-btn crm-btn-primary crm-btn-sm"
                                type="button"
                                onClick={() => history.push(leadSourcesPath())}
                            >
                                Connect Source
                            </button>
                        )}
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={handleExport}>
                            Export
                        </button>
                        <div style={{ position: 'relative' }} ref={leadEntryMenuRef}>
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                type="button"
                                onClick={() => setShowLeadEntryMenu((prev) => !prev)}
                            >
                                Add Lead Options
                            </button>
                            {showLeadEntryMenu && (
                                <div
                                    className="crm-card"
                                    style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 'calc(100% + 8px)',
                                        minWidth: 190,
                                        zIndex: 10,
                                    }}
                                >
                                    <div className="crm-card-body" style={{ padding: 8 }}>
                                        <button
                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                            style={{ width: '100%', marginBottom: 8 }}
                                            type="button"
                                            onClick={() => {
                                                setShowModal(true);
                                                setShowLeadEntryMenu(false);
                                            }}
                                        >
                                            Manual Entry
                                        </button>
                                        <button
                                            className="crm-btn crm-btn-secondary crm-btn-sm"
                                            style={{ width: '100%' }}
                                            type="button"
                                            onClick={() => {
                                                handleImportClick();
                                                setShowLeadEntryMenu(false);
                                            }}
                                        >
                                            Import CSV
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="crm-toolbar-row crm-toolbar-filters">
                    <div className="crm-search">
                        <span className="crm-search-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            className="crm-input crm-search-input"
                            placeholder="Search leads..."
                            value={searchInput}
                            data-crm-search
                            onChange={(event) => {
                                setSearchInput(event.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    <div className="crm-chip-group">
                        {QUICK_STATUS.map((chip) => (
                            <button
                                key={chip.value || 'all'}
                                type="button"
                                className={`crm-chip ${filters.status === chip.value ? 'active' : ''}`}
                                onClick={() => {
                                    setFilters((prev) => ({ ...prev, status: chip.value }));
                                    setPage(1);
                                }}
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>

                    <button
                        className="crm-btn crm-btn-secondary crm-btn-sm"
                        onClick={() => setShowMoreFilters((prev) => !prev)}
                        type="button"
                    >
                        {showMoreFilters ? 'Hide Filters' : 'More Filters'}
                    </button>

                    {hasActiveFilters && (
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={clearFilters}>
                            Clear
                        </button>
                    )}
                </div>

                <div className={`crm-filter-panel ${showMoreFilters ? 'is-open' : ''}`}>
                    <div className="crm-form-group">
                        <label className="crm-label">Source</label>
                        <select
                            className="crm-select"
                            value={filters.source}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, source: event.target.value }));
                                setPage(1);
                            }}
                        >
                            {SOURCE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Assigned To</label>
                        <input
                            type="text"
                            className="crm-input"
                            placeholder="Name or ID"
                            value={filters.owner}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, owner: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Score min</label>
                        <input
                            type="number"
                            className="crm-input"
                            value={filters.scoreMin}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, scoreMin: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Score max</label>
                        <input
                            type="number"
                            className="crm-input"
                            value={filters.scoreMax}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, scoreMax: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Tags</label>
                        <input
                            type="text"
                            className="crm-input"
                            placeholder="tag1, tag2"
                            value={filters.tags}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, tags: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Created from</label>
                        <input
                            type="date"
                            className="crm-input"
                            value={filters.createdFrom}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, createdFrom: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Created to</label>
                        <input
                            type="date"
                            className="crm-input"
                            value={filters.createdTo}
                            onChange={(event) => {
                                setFilters((prev) => ({ ...prev, createdTo: event.target.value }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Needs follow-up</label>
                        <label className="crm-inline-check">
                            <input
                                type="checkbox"
                                checked={filters.needsFollowUp}
                                onChange={(event) => {
                                    setFilters((prev) => ({ ...prev, needsFollowUp: event.target.checked }));
                                    setPage(1);
                                }}
                            />
                            <span>Show overdue or uncontacted</span>
                        </label>
                    </div>
                </div>
            </div>

            {selectedIds.size > 0 && (
                <div className="crm-bulkbar">
                    <div>
                        <strong>{selectedIds.size}</strong> selected
                        {bulkProgress && (
                            <span className="crm-bulk-progress">
                                {bulkProgress.current}/{bulkProgress.total} {bulkProgress.label}
                            </span>
                        )}
                    </div>
                    <div className="crm-bulkbar-actions">
                        <select
                            className="crm-select crm-select-sm"
                            defaultValue=""
                            onChange={(event) => handleBulkStatus(event.target.value)}
                            disabled={bulkLoading}
                        >
                            <option value="">Change status</option>
                            {STATUS_OPTIONS.filter((opt) => opt.value).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <div className="crm-inline-field">
                            <select
                                className="crm-select crm-select-sm"
                                value={bulkAssignee}
                                onChange={(event) => setBulkAssignee(event.target.value)}
                                disabled={bulkLoading || assignmentOptions.length === 0}
                            >
                                <option value="">Assign to...</option>
                                {assignmentOptions.map((option) => (
                                    <option key={option.seatUid} value={option.seatUid}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                onClick={handleBulkAssign}
                                disabled={bulkLoading || !bulkAssignee}
                                type="button"
                            >
                                Assign
                            </button>
                        </div>
                        <div className="crm-inline-field">
                            <input
                                type="text"
                                className="crm-input crm-input-sm"
                                placeholder="Add tag"
                                value={bulkTag}
                                onChange={(event) => setBulkTag(event.target.value)}
                                disabled={bulkLoading}
                            />
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                onClick={handleBulkAddTag}
                                disabled={bulkLoading || !bulkTag.trim()}
                                type="button"
                            >
                                Add
                            </button>
                        </div>
                        <button
                            className="crm-btn crm-btn-danger crm-btn-sm"
                            onClick={handleBulkDelete}
                            disabled={bulkLoading}
                            type="button"
                        >
                            Delete Selected
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : error ? (
                <div className="crm-empty-state">
                    <h3>{error}</h3>
                    <button className="crm-btn crm-btn-primary" onClick={refetch}>Retry</button>
                </div>
            ) : viewMode === 'table' ? (
                <LeadTable
                    leads={leads}
                    currentUser={authUser}
                    assignmentOptions={assignmentOptions}
                    selectedIds={selectedIds}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                    onAssign={async (leadId, assigneeSeatUid) => {
                        const selected = assignmentOptions.find((option) => option.seatUid === assigneeSeatUid);
                        if (!selected) return;
                        try {
                            await update(leadId, {
                                assignedTo: selected.seatUid,
                                assignedToName: selected.name,
                                ownerSeatId: selected.seatId,
                            });
                            showSuccess(`Assigned to ${selected.name}`);
                            refetch();
                        } catch (err) {
                            console.error('Failed to assign lead:', err);
                            showError('Failed to assign lead');
                        }
                    }}
                    onStatusChange={handleStatusChange}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddTask={handleAddTask}
                    onQuickAction={handleQuickActionLog}
                />
            ) : (
                <div className="crm-card-grid">
                    {leads.map((lead) => (
                        <LeadCard
                            key={lead.id}
                            lead={lead}
                            currentUser={authUser}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {!loading && !error && leads.length === 0 && (
                <div className="crm-empty-state">
                    <h3>No leads found</h3>
                    <p>{hasActiveFilters ? 'Try adjusting your filters.' : 'Get started by adding your first lead.'}</p>
                    {!hasActiveFilters && (
                        <button className="crm-btn crm-btn-primary" onClick={() => setShowLeadEntryMenu(true)}>
                            Add Lead Options
                        </button>
                    )}
                </div>
            )}

            {(total || 0) > 0 && (
                <div className="crm-pagination">
                    <span className="crm-pagination-summary">
                        Showing {pageStart}-{pageEnd} of {total} leads
                    </span>
                    <div className="crm-pagination-controls">
                        <div className="crm-pagination-control-group">
                            <label className="crm-label" style={{ marginBottom: 0 }}>Rows</label>
                            <select
                                className="crm-select crm-select-sm"
                                value={pageSize}
                                onChange={(event) => setPageSize(Number(event.target.value) || 10)}
                                style={{ width: 'auto', minWidth: '84px' }}
                            >
                                {LEADS_PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>{size} / page</option>
                                ))}
                            </select>
                        </div>
                        <div className="crm-pagination-control-group">
                            <span className="crm-pagination-summary" style={{ whiteSpace: 'nowrap' }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <label className="crm-label" style={{ marginBottom: 0 }}>Go to</label>
                            <select
                                className="crm-select crm-select-sm"
                                value={currentPage}
                                onChange={(event) => setPage(Number(event.target.value) || 1)}
                                style={{ width: 'auto', minWidth: '78px' }}
                                aria-label="Go to page"
                            >
                                {Array.from({ length: totalPages }, (_, index) => {
                                    const pageNumber = index + 1;
                                    return (
                                        <option key={pageNumber} value={pageNumber}>
                                            {pageNumber}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <button
                            className="crm-btn crm-btn-secondary crm-btn-sm"
                            onClick={() => setPage((prev) => prev - 1)}
                            disabled={currentPage <= 1}
                        >
                            Previous
                        </button>
                        <button
                            className="crm-btn crm-btn-secondary crm-btn-sm"
                            onClick={() => setPage((prev) => prev + 1)}
                            disabled={currentPage >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="crm-modal-overlay" onClick={handleCloseModal}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '600px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{editingLead ? 'Edit Lead' : 'Add Lead'}</h2>
                            <button className="crm-modal-close" onClick={handleCloseModal}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <LeadForm
                                lead={editingLead}
                                onSubmit={handleSubmit}
                                onCancel={handleCloseModal}
                                loading={mutationLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {activityLead && (
                <div className="crm-modal-overlay" onClick={() => setActivityLead(null)}>
                    <div
                        className="crm-modal"
                        onClick={(event) => event.stopPropagation()}
                        style={{ background: 'var(--crm-bg)', maxWidth: '600px', width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Log Activity</h2>
                            <button className="crm-modal-close" onClick={() => setActivityLead(null)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <ActivityForm
                                activity={createActivitySeed(activityLead)}
                                onSubmit={handleActivitySubmit}
                                onCancel={() => setActivityLead(null)}
                                loading={activityLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                entity="leads"
                onImported={() => {
                    refetch();
                }}
            />

            <ConfirmDialog />
        </div>
    );
};

export default LeadsPage;
